import { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../utils/googleMaps';
import Layout from '../components/Layout';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  CloudRain, Play, Pause, SkipBack, SkipForward, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache config
const RAINVIEWER_CACHE_KEY = 'rainviewer_meta_v1';
const RAINVIEWER_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 42.7,
  lng: 25.5
};

// Single layer config - RainViewer radar only
const RADAR_LAYER = {
  id: 'radar',
  name: 'Radar / Precipitation',
  icon: CloudRain,
  description: 'RainViewer radar overlay',
  tileUrl: 'https://tilecache.rainviewer.com/v2/radar/{timestamp}/256/{z}/{x}/{y}/2/1_1.png',
  color: 'var(--gold)'
};

// LocalStorage cache helpers
const getCachedMeta = () => {
  try {
    const cached = localStorage.getItem(RAINVIEWER_CACHE_KEY);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > RAINVIEWER_CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
};

const setCachedMeta = (data) => {
  try {
    localStorage.setItem(RAINVIEWER_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {}
};

const WeatherMap = () => {
  const { user } = useAuth();
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);
  
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [activeLayers, setActiveLayers] = useState(['radar']);
  const [layerOpacity, setLayerOpacity] = useState(0.7);
  const [radarTimestamps, setRadarTimestamps] = useState([]);
  const [currentTimeIndex, setCurrentTimeIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const playIntervalRef = useRef(null);
  const tileOverlayRef = useRef(null);

  // Load RainViewer timestamps
  const loadRainviewerMeta = useCallback(async () => {
    // Only fetch when visible
    if (document.visibilityState !== 'visible') return;
    
    // Check cache first
    const cached = getCachedMeta();
    if (cached) {
      const timestamps = extractTimestamps(cached);
      if (timestamps.length > 0) {
        setRadarTimestamps(timestamps);
        setCurrentTimeIndex(timestamps.length - 1); // Start at latest
        setLastUpdate(new Date());
        return;
      }
    }
    
    try {
      const response = await axios.get(`${API}/weather/rainviewer/meta`);
      const data = response.data;
      setCachedMeta(data);
      
      const timestamps = extractTimestamps(data);
      setRadarTimestamps(timestamps);
      setCurrentTimeIndex(timestamps.length - 1); // Start at latest
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading RainViewer meta:', error);
      toast.error('Failed to load radar data');
    }
  }, []);

  const extractTimestamps = (data) => {
    const timestamps = [];
    if (data?.radar?.past) {
      // RainViewer API returns {time: unix_ts, path: "/v2/radar/hash"}
      // We need the time for display and the path for tile URLs
      timestamps.push(...data.radar.past.map(t => ({
        time: t.time,
        path: t.path
      })));
    }
    if (data?.radar?.nowcast) {
      timestamps.push(...data.radar.nowcast.map(t => ({
        time: t.time,
        path: t.path
      })));
    }
    return timestamps;
  };

  // Initial load + visibility refresh
  useEffect(() => {
    loadRainviewerMeta();
    
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const cached = getCachedMeta();
        if (!cached) loadRainviewerMeta();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadRainviewerMeta]);

  // Center on user's farm
  useEffect(() => {
    if (user?.latitude && user?.longitude) {
      setCenter({ lat: user.latitude, lng: user.longitude });
    }
  }, [user]);

  // SINGLE EFFECT for overlay management
  useEffect(() => {
    if (!map || !isLoaded) return;
    
    // Clear existing overlay
    if (map.overlayMapTypes) {
      map.overlayMapTypes.clear();
      tileOverlayRef.current = null;
    }
    
    // Exit if layer off or no timestamps
    if (activeLayers.length === 0 || radarTimestamps.length === 0) {
      return;
    }
    
    // Get current timestamp (clamped)
    const maxIndex = radarTimestamps.length - 1;
    const clampedIndex = Math.max(0, Math.min(currentTimeIndex, maxIndex));
    const currentFrame = radarTimestamps[clampedIndex];
    
    if (!currentFrame || !currentFrame.path) return;
    
    // Create tile layer using RainViewer path
    // Format: https://tilecache.rainviewer.com{path}/256/{z}/{x}/{y}/2/1_1.png
    const tileLayer = new window.google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => {
        return `https://tilecache.rainviewer.com${currentFrame.path}/256/${zoom}/${coord.x}/${coord.y}/2/1_1.png`;
      },
      tileSize: new window.google.maps.Size(256, 256),
      opacity: layerOpacity,
      name: RADAR_LAYER.name
    });
    
    map.overlayMapTypes.insertAt(0, tileLayer);
    tileOverlayRef.current = tileLayer;
    
  }, [map, isLoaded, activeLayers, radarTimestamps, currentTimeIndex, layerOpacity]);

  // Toggle radar on/off
  const toggleLayer = () => {
    setActiveLayers(prev => prev.length > 0 ? [] : ['radar']);
  };

  // Playback controls
  const maxIndex = Math.max(0, radarTimestamps.length - 1);
  
  const handlePlay = () => {
    if (isPlaying) {
      clearInterval(playIntervalRef.current);
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      playIntervalRef.current = setInterval(() => {
        setCurrentTimeIndex(prev => prev >= maxIndex ? 0 : prev + 1);
      }, 500);
    }
  };

  const handleStepBack = () => {
    setCurrentTimeIndex(prev => Math.max(0, prev - 1));
  };

  const handleStepForward = () => {
    setCurrentTimeIndex(prev => Math.min(maxIndex, prev + 1));
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    };
  }, []);

  const onMapLoad = useCallback((map) => setMap(map), []);

  // Time label from timestamp
  const getCurrentTimeLabel = () => {
    if (radarTimestamps.length === 0) return 'Loading...';
    const idx = Math.max(0, Math.min(currentTimeIndex, maxIndex));
    const frame = radarTimestamps[idx];
    if (!frame || !frame.time) return 'Loading...';
    return new Date(frame.time * 1000).toLocaleString('en-GB', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div data-testid="weather-map-page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', gap: 16, padding: '0 4px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Weather Map</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Radar overlay powered by RainViewer. Forecast data from MET Norway.
              {lastUpdate && <span> • Updated {lastUpdate.toLocaleTimeString()}</span>}
            </p>
          </div>
        </div>

        {/* Main Content - responsive flex */}
        <div style={{ flex: 1, display: 'flex', gap: 16, minHeight: 0, flexDirection: 'column' }} className="weather-map-content">
          {/* Map + Timeline (full width on mobile) */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, order: 1 }} className="weather-map-main">
            {/* Map */}
            <div className="card-floating" style={{ flex: 1, padding: 0, overflow: 'hidden', position: 'relative', minHeight: 300 }}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={center}
                  zoom={6}
                  onLoad={onMapLoad}
                  options={{
                    mapTypeId: 'terrain',
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    minZoom: 3,
                    maxZoom: 12
                  }}
                />
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="spinner" />
                </div>
              )}

              {/* Time overlay */}
              <div style={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--panel-strong)',
                backdropFilter: 'blur(10px)',
                padding: '8px 16px',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 600,
                border: '1px solid var(--border-soft)'
              }}>
                {getCurrentTimeLabel()}
              </div>

              {/* Attribution */}
              <div style={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                background: 'var(--panel-strong)',
                backdropFilter: 'blur(10px)',
                padding: '8px 12px',
                borderRadius: 10,
                fontSize: 11,
                color: 'var(--text-muted)',
                border: '1px solid var(--border-soft)'
              }}>
                <MapPin size={12} style={{ display: 'inline', marginRight: 4 }} />
                RainViewer
              </div>
            </div>

            {/* Timeline Scrubber */}
            <div className="card-floating" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={handleStepBack} className="btn-depth-ghost" style={{ padding: 8 }} data-testid="timeline-back">
                  <SkipBack size={18} />
                </button>
                <button onClick={handlePlay} className="btn-depth-primary" style={{ padding: 10 }} data-testid="timeline-play">
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>
                <button onClick={handleStepForward} className="btn-depth-ghost" style={{ padding: 8 }} data-testid="timeline-forward">
                  <SkipForward size={18} />
                </button>
              </div>

              {/* Slider */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Past</span>
                <input
                  type="range"
                  min="0"
                  max={maxIndex}
                  value={Math.min(currentTimeIndex, maxIndex)}
                  onChange={(e) => setCurrentTimeIndex(parseInt(e.target.value))}
                  style={{ flex: 1, accentColor: 'var(--gold)' }}
                  data-testid="timeline-slider"
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Nowcast</span>
              </div>

              {/* Frame counter */}
              <div style={{ 
                padding: '6px 12px', 
                background: 'rgba(255,255,255,0.06)', 
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                minWidth: 70,
                textAlign: 'center'
              }}>
                {currentTimeIndex + 1} / {radarTimestamps.length || 1}
              </div>
            </div>
          </div>
          
          {/* Sidebar - below map on mobile */}
          <aside className="card-floating weather-map-sidebar" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16, flexShrink: 0, order: 2 }}>
            {/* Single Layer Toggle */}
            <div>
              <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Layer</h3>
              <button
                onClick={toggleLayer}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 10,
                  width: '100%',
                  border: activeLayers.length > 0 ? `2px solid ${RADAR_LAYER.color}` : '1px solid var(--border-soft)',
                  background: activeLayers.length > 0 ? 'rgba(255,255,255,0.08)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
                data-testid="layer-toggle-radar"
              >
                <CloudRain size={20} style={{ color: RADAR_LAYER.color }} />
                <div style={{ textAlign: 'left' }}>
                  <p style={{ fontWeight: 600, fontSize: 13 }}>{RADAR_LAYER.name}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>{RADAR_LAYER.description}</p>
                </div>
              </button>
            </div>

            {/* Opacity Slider */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, display: 'block' }}>
                Opacity: {Math.round(layerOpacity * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={layerOpacity * 100}
                onChange={(e) => setLayerOpacity(e.target.value / 100)}
                style={{ width: '100%', accentColor: 'var(--gold)' }}
                data-testid="opacity-slider"
              />
            </div>

            {/* Info */}
            <p style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Radar reflectivity overlay shows precipitation intensity. Data updates every 10 minutes.
            </p>
          </aside>
        </div>
      </div>
      
      {/* Responsive styles for weather map */}
      <style>{`
        @media (min-width: 769px) {
          .weather-map-content {
            flex-direction: row !important;
          }
          .weather-map-sidebar {
            width: 220px;
            order: -1 !important;
          }
          .weather-map-main {
            order: 0 !important;
          }
        }
        @media (max-width: 768px) {
          .weather-map-content {
            flex-direction: column !important;
          }
          .weather-map-sidebar {
            width: 100% !important;
          }
          .weather-map-main {
            min-height: 350px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default WeatherMap;
